'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MapRef } from 'react-map-gl/mapbox';
import dynamic from 'next/dynamic';
import { type CompareProduct, SearchModes } from '@/domainLogic/index.ts';
import { useSearchPageData } from '@/app/components/search/searchPageDataProvider.tsx';
import type { SearchParams } from '@/server/searchParams.ts';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { PERSISTENT_FILTER_PARAMS, URLParameters } from '@/app/common/urlParameters.ts';
import { type CompareResponse } from '@/app/api/search/compare/createCompareData.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useSearchPageWrapperData } from '@/app/components/search/searchPageCommonWrapperProvider.tsx';
import { SearchDealsPageContext } from '@/app/components/search/searchDealsPageProvider.tsx';
import { type SearchCommonPageData } from '@/app/components/search/SearchMain.tsx';
import { useCuriosityManager } from '@/app/common/CuriosityManagerProvider.tsx';
import { updateQueryStringParam } from '@/utils/queryUtils.ts';
import { useCanShowModal } from '@/app/components/search/hooks/useCanShowModal.ts';
import useIsClient from '@/app/components/search/hooks/useIsClient.ts';
import { getDesktopViewModeCookieKey, toggleListViewValue, ViewMode } from '@/app/common/viewModeUtils.ts';
import { LAYOUT_TYPE } from '@/app/components/search/components/LayoutManager/utils.ts';
import { removeParameters } from '@/app/common/urlParametersUtils.ts';
import type { SortValues } from '@/app/common/memsearch/types.ts';
import { createSearchParamsAccommodationFilter } from '@/app/components/search/filters/createSearchParamsAccommodationFilter.ts';
import { useViewOnMap } from '@/app/components/search/components/EvolutionCard/Details/useViewOnMap.ts';
import { hotjarEvents, HotjarEvent } from '@/app/common/hotjar/hotjarEvents.ts';
import useCookieConsent from '@/app/common/hooks/useCookieConsent';
import { createResultPageShowMapClickedPayload } from '@/app/common/starTrack/events/resultPageShowMapClicked.ts';
import { watchSearchLoading } from '@/app/components/search/logging/searchLoadingWatchdog.ts';
import LayoutManager from '../LayoutManager';
import type Form from '../Form';
import { createOffsetFromPage } from '../../utils/pagerUtils';
import { APIStatusEnum } from '../../common/reloadData/APIStatusEnum';
import { RouteType } from '../../Curiosity';
import getSearchParams, { getSearchParamsClient } from '../../common/getSearchParams';
import { enforceSearchId, initialization } from './utils';
import { logClientSideTimings } from '../../utils/timings/timingsClient';
import { SearchPageMain } from './styled';
import { COOKIE_KEY } from '../../utils/Cookies/types';
import { getCookie, removeCookie, setCookie } from '../../utils/Cookies/clientCookies';
import { removeAllFilters } from '../Filters/utils';
import { createSearchParamsPinkPeopleFromFilters } from '../../pinkpeople/clientPinkPeople';
import { sumClasses } from '../../utils/misc';
import { useIsDesktop, useIsMobile, useIsTablet } from '../../utils/useSsrMedia';
import { SearchPageContext } from './SearchPageContext.ts';
import { useViewMode } from './useViewMode.ts';
import type { DealCard } from '../../utils/buildDealCards/dealCardTypes.ts';
import type { HandleWaitingCallFinished } from '../../common/waitingCallGeneric.ts';
import { SearchCommonPageContext } from '../../searchCommonPageProvider.tsx';

export type SearchDealsPageData = {
  hasPreselectedFlight: boolean;
  hasPreselectedFlightLink: boolean;
  handleViewModeSwitch: () => void;
  handleShowMap: () => void;
  highlightedHotelId?: number | null;
  handleViewMapClick?: (e: React.MouseEvent<HTMLButtonElement>, hotelId: number) => void;
};

export type HandleMapChanged = {
  searchParams: SearchParams;
  shouldLaunchSearchChanged: boolean;
};

export type HandleSortChanged = {
  newSortValue: SortValues;
  searchParamsUpdated: SearchParams;
};

export type HandleFiltersChanged = {
  searchParams: SearchParams;
  appliedFilters: SearchParams;
};

export type DealToCompare = {
  hotelId: number;
  image?: string;
  dealCard?: DealCard;
  compareProduct?: CompareProduct;
};

const FiltersModal = dynamic(() => import('@/app/components/search/components/Modal/Modals/FiltersModal'), {
  ssr: false,
});

export type SearchPageContainerProps = {
  mapRef: React.RefObject<MapRef | null>;
};

const SearchPageContainer = ({ mapRef }: SearchPageContainerProps) => {
  const isClient = useIsClient();
  const {
    searchParams: searchParamsServer,
    searchInfoData,
    localization,
    curiosityParams,
    commonData,
    viewMode: viewModeServer,
  } = usePageWrapperData();
  const { showFilters, handleSortChangedCommon, handleShowFiltersCommon } = useSearchPageWrapperData();
  const { productsData, initialDealsToCompare, isLoading, dynamicFilters } = useSearchPageData();
  const dynamicFiltersResponse = dynamicFilters?.response;
  const { starTrack, handleSearchChanged, handleNewSearch, reportListView } = useCommonClient();
  const { curiosityClickEvents } = useCuriosityManager();
  const { searchMode, searchId } = searchInfoData;
  const vcSearchId = productsData?.vcSearchId || '';
  const { isDesktopDevice, isTabletDevice, isMobileDevice } = commonData;
  const isMobile = useIsMobile(isMobileDevice);
  const isTablet = useIsTablet(isTabletDevice);
  const isDesktop = useIsDesktop(isDesktopDevice);
  const { viewMode, setViewMode, listView, layoutType } = useViewMode(viewModeServer, isDesktop);
  useEffect(() => reportListView(listView), [listView, reportListView]);
  const { businessProfile } = localization;
  const routeType = RouteType.OSMAP;
  const listContainerRef = useRef<HTMLDivElement>(undefined);
  const verticalFiltersRef = useRef<Form>(undefined);
  const previousLayoutTypeRef = useRef<LAYOUT_TYPE | null>(null);
  const APIStatus = productsData?.apiStatus || APIStatusEnum.NORESULTS;
  const isWaiting = APIStatus === APIStatusEnum.WAIT;
  // Report a loading state that never ends. These are the two flags that keep the placeholder templates
  // on screen — ListContainer recomputes the same pair and gates the cards grid on them.
  useEffect(() => {
    watchSearchLoading(isLoading || isWaiting);
  }, [isLoading, isWaiting]);
  const offsetFromServer = createOffsetFromPage(searchParamsServer.page);
  const offsetFromClient = createOffsetFromPage(getSearchParams()?.page);
  const [offset, setOffset] = useState(offsetFromServer);
  const [showTaxesIncludedTooltip, setShowTaxesIncludedTooltip] = useState(false);
  const taxesIncludedTooltipClaimedRef = useRef(false);

  const triggerTaxesIncludedTooltip = useCallback(() => {
    taxesIncludedTooltipClaimedRef.current = false;
    setShowTaxesIncludedTooltip(true);
  }, []);

  const dismissTaxesIncludedTooltip = useCallback(() => {
    setShowTaxesIncludedTooltip(false);
  }, []);

  const [cardOverHotelId, setCardOverHotelId] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (offset !== offsetFromClient) {
      setOffset(offsetFromClient);
    }
  }, [offset, offsetFromClient]);

  const [forceFullscreenMapDesktop] = useState(false);
  const { canShowModal } = useCanShowModal(showFilters);
  const [dealsToCompare, setDealsToCompare] = useState<DealToCompare[]>(initialDealsToCompare);
  const [canShowComparisonPromo, setCanShowComparisonPromo] = useState(
    getCookie(COOKIE_KEY.COMPARISON_PROMO) !== 'true' && dealsToCompare?.length === 0
  );

  const dealsCount = productsData?.productCounters?.productsCount;
  const readyNoResults = APIStatus !== APIStatusEnum.WAIT && !dealsCount;
  const transport = productsData?.transport;
  const isTransportEnabled = transport?.usecaseEnabled;
  const opxWaiting = localization?.configValuesApp?.opxEnabled && isWaiting;
  const evolutionResultsOnlyEnabled = localization?.configValuesApp?.evolutionResultsOnlyEnabled;
  const evolutionEnabled = localization?.configValuesApp?.evolutionEnabled;
  const listViewDesktopCookieKey = getDesktopViewModeCookieKey(localization);
  const isStandardTsxFlow = !evolutionResultsOnlyEnabled;
  const apexEnabledForResults = localization.configValuesApp?.apexEnabledForResults;
  const hasConsent = useCookieConsent(commonData.isLocal);
  const evoFired = useRef(false);
  const apexFired = useRef(false);
  const hasPreselectedFlightLink = !!(
    isTransportEnabled &&
    !opxWaiting &&
    searchMode !== SearchModes.HO &&
    !readyNoResults
  );
  const hasPreselectedFlight = hasPreselectedFlightLink;

  const getIdsToCompareState = (): number[] => {
    const compareIds = getSearchParamsClient(searchParamsServer)?.compareIds || undefined;
    return (compareIds?.split(';') || []).map((id) => parseInt(id, 10));
  };

  const setIdsToCompareState = (hotelId: number, toBeAdded: boolean) => {
    const prevIdsToCompare = getIdsToCompareState().slice(0, 5);
    const newIdsToCompare = toBeAdded
      ? [...prevIdsToCompare, hotelId]
      : prevIdsToCompare.filter((id) => id !== hotelId);
    updateQueryStringParam(URLParameters.COMPARE_IDS, newIdsToCompare.join(';'));
  };

  const addDealCompare = (hotelId: number) => {
    setIdsToCompareState(hotelId, true);
    const dealCard = productsData?.dealCards?.find((d) => d?.product?.accommodation?.hotelId === hotelId);
    const image = dealCard?.product?.accommodation?.image;
    setDealsToCompare((prev) => [
      ...prev,
      {
        hotelId,
        image,
        dealCard,
      },
    ]);
  };

  const removeDealCompare = (hotelId: number) => {
    setIdsToCompareState(hotelId, false);
    setDealsToCompare((prev) => prev.filter((prevDealToCompare) => prevDealToCompare?.hotelId !== hotelId));
  };

  const handleDealCompareAddClicked = (hotelId: number) => {
    const prevIdsToCompare = getIdsToCompareState();
    const toBeAdded = !prevIdsToCompare.includes(hotelId);
    if (toBeAdded) {
      curiosityClickEvents.comparisonAdd();
      addDealCompare(hotelId);
    } else {
      curiosityClickEvents.comparisonRemove();
      removeDealCompare(hotelId);
    }
  };

  const handleDealCompareRemoveClicked = (hotelId: number) => {
    removeDealCompare(hotelId);
  };

  const handleCompareCallFinished = ({ response }: HandleWaitingCallFinished<CompareResponse>) => {
    setDealsToCompare((prev) =>
      prev.map((prevDealToCompare) => {
        const compareProduct = response?.products?.find(
          (p: CompareProduct) => p?.accommodation?.hotelId === prevDealToCompare?.hotelId
        );
        return compareProduct ? { ...prevDealToCompare, compareProduct } : prevDealToCompare;
      })
    );
  };

  const removePageFromParamsAndResetOffset = (searchParams: SearchParams) => {
    const searchParamsCleaned = removeParameters(searchParams, [URLParameters.PAGE, URLParameters.OFFSET]);
    setOffset(0);

    return searchParamsCleaned;
  };

  const handleMapChanged = ({ searchParams, shouldLaunchSearchChanged }: HandleMapChanged) => {
    if (shouldLaunchSearchChanged) {
      const searchParamsCleaned = removePageFromParamsAndResetOffset(searchParams);

      handleSearchChanged({ newSearchParams: searchParamsCleaned, shouldAddBrowserHistory: false });
    }
  };

  const handleSortChanged = ({ newSortValue, searchParamsUpdated }: HandleSortChanged) => {
    const { searchParamsNormalised } = handleSortChangedCommon({ newSortValue, searchParamsUpdated });
    const searchParamsCleaned = removePageFromParamsAndResetOffset(searchParamsNormalised);

    handleSearchChanged({ newSearchParams: searchParamsCleaned });
  };

  const handleFiltersChanged = ({ searchParams, appliedFilters }: HandleFiltersChanged) => {
    const searchParamsAccommodationFilter = createSearchParamsAccommodationFilter({ searchParams, appliedFilters });
    const searchParamsPinkPeople = createSearchParamsPinkPeopleFromFilters({
      searchParams: searchParamsAccommodationFilter,
      searchInfoData,
      appliedFilters,
      commonData,
      localization,
    });

    const baseParams = searchParamsPinkPeople || searchParamsAccommodationFilter;
    const searchParamsWithoutFilters = removeAllFilters(baseParams, { exclude: PERSISTENT_FILTER_PARAMS });
    const searchParamsCleaned = removePageFromParamsAndResetOffset(searchParamsWithoutFilters);

    const hasPinkPeopleFilterChanged = !!searchParamsPinkPeople;
    if (hasPinkPeopleFilterChanged) {
      handleNewSearch({
        ...searchParamsCleaned,
        ...appliedFilters,
      });
    } else {
      handleSearchChanged({
        newSearchParams: {
          ...searchParamsCleaned,
          ...appliedFilters,
        },
      });
    }
  };

  const handleShowFilters = (newShowFilters: boolean) => {
    handleShowFiltersCommon({ newShowFilters, routeType });
  };

  // When map is hidden (layout changes from map to list), remove map coordinates from URL and perform a new search to reset the results
  useEffect(() => {
    if (!isClient) return;

    const wasShowingMap =
      previousLayoutTypeRef.current === LAYOUT_TYPE.DESKTOP_MAP ||
      previousLayoutTypeRef.current === LAYOUT_TYPE.MOBILE_MAP;
    const isNowShowingList = layoutType === LAYOUT_TYPE.DESKTOP_LIST || layoutType === LAYOUT_TYPE.MOBILE_LIST;

    // If we transitioned from showing map to showing list
    if (wasShowingMap && isNowShowingList) {
      const currentSearchParams = getSearchParams();
      if (currentSearchParams[URLParameters.MAP_AREA]) {
        const searchParamsWithoutMap = removeParameters(currentSearchParams, [
          URLParameters.MAP_AREA,
          URLParameters.PAGE,
        ]);
        handleSearchChanged({ newSearchParams: searchParamsWithoutMap, shouldAddBrowserHistory: false });
      }
    }

    // Always update the previous layout type
    previousLayoutTypeRef.current = layoutType;
  }, [layoutType, isClient]);

  useEffect(() => {
    if (isClient) {
      initialization({
        searchParams: searchParamsServer,
        curiosityParams,
        listContainerRef,
        listView,
        isMobile,
        isDesktop,
        isTablet,
      });
    }
  }, [isClient]);

  useEffect(() => {
    enforceSearchId(searchId, vcSearchId, businessProfile);
    logClientSideTimings({ commonData, localization, curiosityParams, searchInfoData });
  }, []);

  useEffect(() => {
    if (hasConsent && (evolutionResultsOnlyEnabled || evolutionEnabled) && !evoFired.current) {
      evoFired.current = true;
      hotjarEvents([HotjarEvent.EVO_RESULTS]);
    }
  }, [hasConsent, evolutionResultsOnlyEnabled, evolutionEnabled]);

  useEffect(() => {
    if (hasConsent && apexEnabledForResults && !apexFired.current) {
      apexFired.current = true;
      hotjarEvents([HotjarEvent.EVO_RESULTS_APEX]);
    }
  }, [hasConsent, apexEnabledForResults]);

  const handleViewModeSwitch = useCallback(() => {
    const searchParamsFresh = getSearchParams();
    const newValue = toggleListViewValue(isDesktop, viewMode);

    if (!isClient) return;

    setViewMode((prevState) => ({
      desktop: isDesktop ? newValue : prevState.desktop,
      mobile: !isDesktop ? newValue : prevState.mobile,
    }));

    if (isDesktop) {
      if (isStandardTsxFlow) {
        removeCookie(COOKIE_KEY.LIST_VIEW_DESKTOP_ABTEST);
      }
      setCookie(listViewDesktopCookieKey, newValue, 30);
    }

    starTrack.resultPageShowMapClicked(
      createResultPageShowMapClickedPayload({ searchId, action: newValue === ViewMode.LIST ? 'hide' : 'show' })
    );
    // starTrack.resultPageMapListViewChanged({ searchId, listView: newValue === ViewMode.LIST });
    curiosityClickEvents.viewModeToggle(newValue);

    updateQueryStringParam(URLParameters.VIEW_MODE_DESKTOP, newValue);

    if (searchParamsFresh[URLParameters.MAP_AREA]) {
      const searchParamsFresher = getSearchParams();
      handleSearchChanged({ newSearchParams: searchParamsFresher, isNewSearch: true, isChangeListView: true });
    }
  }, [curiosityClickEvents, isClient, isDesktop, listView, searchId, starTrack]);

  const handleShowMap = useCallback(() => {
    if (!isClient) return;
    const newValue = toggleListViewValue(isDesktop, viewMode);

    setViewMode((prevState) => ({
      desktop: isDesktop ? newValue : prevState.desktop,
      mobile: !isDesktop ? newValue : prevState.mobile,
    }));

    if (isDesktop) {
      setCookie(listViewDesktopCookieKey, newValue, 30);
      updateQueryStringParam(URLParameters.VIEW_MODE_DESKTOP, newValue);
    }

    // TODO: to check if we want to change these events for the case when user opens the map clicking on the city name
    // starTrack.resultPageMapListViewChanged({ searchId, listView: newValue === ViewMode.LIST });
    curiosityClickEvents.viewModeToggle(newValue);
  }, [curiosityClickEvents, isClient, isDesktop, searchId, setViewMode, starTrack, viewMode]);

  const { highlightedHotelId, handleViewMapClick } = useViewOnMap(handleShowMap, () =>
    starTrack.resultPageShowMapClicked(createResultPageShowMapClickedPayload({ searchId, action: 'show_from_card' }))
  );

  const searchPageContextValue = useMemo(
    () => ({
      listView,
      layoutType,
      dealsToCompare,
      canShowComparisonPromo,
      setCanShowComparisonPromo,
      handleDealCompareAddClicked,
      handleDealCompareRemoveClicked,
      handleCompareCallFinished,
      showTaxesIncludedTooltip,
      triggerTaxesIncludedTooltip,
      dismissTaxesIncludedTooltip,
      taxesIncludedTooltipClaimedRef,
    }),
    [listView, layoutType, dealsToCompare, canShowComparisonPromo, showTaxesIncludedTooltip]
  );

  const searchCommonPageContextValue: SearchCommonPageData = useMemo(
    () => ({
      handleSortChanged,
      handleFiltersChanged,
      handleMapChanged,
    }),
    [handleSortChanged, handleFiltersChanged, handleMapChanged]
  );

  const searchDealsPageContentValue: SearchDealsPageData = useMemo(
    () => ({
      hasPreselectedFlight,
      hasPreselectedFlightLink,
      handleViewModeSwitch,
      handleShowMap,
      handleViewMapClick,
      highlightedHotelId,
    }),
    [
      hasPreselectedFlight,
      hasPreselectedFlightLink,
      handleViewModeSwitch,
      handleShowMap,
      handleViewMapClick,
      highlightedHotelId,
    ]
  );

  return (
    <SearchPageContext.Provider value={searchPageContextValue}>
      <SearchCommonPageContext.Provider value={searchCommonPageContextValue}>
        <SearchDealsPageContext.Provider value={searchDealsPageContentValue}>
          <SearchPageMain
            layoutType={layoutType}
            evolutionEnabled={localization?.configValuesApp?.evolutionResultsOnlyEnabled}
            className={sumClasses([
              'openx-ui-main-collapsed',
              isLoading || isWaiting ? 'openx-ui-main-loading' : '',
              readyNoResults ? 'no-results' : '',
            ])}
          >
            <LayoutManager
              routeType={routeType}
              isWaiting={isWaiting}
              dynamicFiltersResponse={dynamicFiltersResponse}
              verticalFiltersRef={verticalFiltersRef}
              listContainerRef={listContainerRef}
              offset={offset}
              setCardOverHotelId={setCardOverHotelId}
              setOffset={setOffset}
              mapRef={mapRef}
              showFilters={showFilters}
              cardOverHotelId={cardOverHotelId}
              forceFullscreenMapDesktop={forceFullscreenMapDesktop}
              layoutType={layoutType}
              handleShowFilters={handleShowFilters}
            />
          </SearchPageMain>
          {canShowModal && (
            <FiltersModal
              dynamicFiltersResponse={dynamicFiltersResponse && dynamicFiltersResponse.filters}
              isMap={true}
              handleShowFilters={handleShowFilters}
              showFilters={showFilters}
            />
          )}
        </SearchDealsPageContext.Provider>
      </SearchCommonPageContext.Provider>
    </SearchPageContext.Provider>
  );
};

export default SearchPageContainer;
